/**
 * Honorarverträge je Honorarkraft verwalten – Laufzeit + Preis (Stundensatz
 * und/oder Pauschale) + optionaler Vertrags-PDF-Upload. Mehrere Verträge
 * (Historie) möglich.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, Plus, Trash2, FileText, Upload, Pencil } from 'lucide-react';
import { honorarkraefteAPI, uploadAPI } from '../../lib/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { art: 'einzelbeauftragung', bezeichnung: '', start_date: '', end_date: '', stundensatz: '', abrechnungsart: 'Zeitstunde', pauschale: '', notes: '', document: null };
const ART_LABEL = { rahmenvertrag: 'Rahmenvertrag', einzelbeauftragung: 'Einzelbeauftragung', zusatz: 'Zusatz/Startdokument' };
const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const fmtDate = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return iso; } };
const fmtEUR = (v) => (v == null ? '' : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v));

export const ContractsDialog = ({ open, onClose, honorarkraft, onChanged }) => {
  const [hk, setHk] = useState(honorarkraft);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { setHk(honorarkraft); if (open) { setShowForm(false); setForm(EMPTY); setEditId(null); } }, [honorarkraft, open]);

  if (!hk) return null;
  const contracts = hk.contracts || [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (c) => {
    setForm({
      art: c.art || 'einzelbeauftragung',
      bezeichnung: c.bezeichnung || '', start_date: c.start_date || '', end_date: c.end_date || '',
      stundensatz: c.stundensatz ?? '', abrechnungsart: c.abrechnungsart || 'Zeitstunde',
      pauschale: c.pauschale ?? '', notes: c.notes || '', document: c.document || null,
    });
    setEditId(c.id); setShowForm(true);
  };

  // Schnell-Anlage einer Einzelbeauftragung für einen Monat (Satz vom Rahmenvertrag erben)
  const createMonthly = async (year, monthIdx) => {
    const rahmen = contracts.find((c) => c.art === 'rahmenvertrag');
    const first = new Date(Date.UTC(year, monthIdx, 1)).toISOString().slice(0, 10);
    const last = new Date(Date.UTC(year, monthIdx + 1, 0)).toISOString().slice(0, 10);
    try {
      const res = await honorarkraefteAPI.addContract(hk.id, {
        art: 'einzelbeauftragung',
        bezeichnung: `Einzelbeauftragung ${MONTHS[monthIdx]} ${year}`,
        start_date: first, end_date: last,
        stundensatz: rahmen?.stundensatz ?? null,
        abrechnungsart: rahmen?.abrechnungsart || 'Zeitstunde',
        pauschale: rahmen?.pauschale ?? null,
      });
      setHk(res.data); onChanged?.();
      toast.success(`Einzelbeauftragung ${MONTHS[monthIdx]} ${year} angelegt`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anlegen fehlgeschlagen');
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAPI.uploadCertificate(file, { document_title: `Honorarvertrag_${hk.last_name}` });
      set('document', { filename: res.data.filename, original_filename: res.data.original_filename, url: res.data.url });
      toast.success('Vertrag hochgeladen');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (form.art === 'rahmenvertrag' && form.stundensatz === '' && form.pauschale === '') {
      toast.error('Beim Rahmenvertrag bitte Stundensatz und/oder Pauschale angeben');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        art: form.art || 'einzelbeauftragung',
        bezeichnung: form.bezeichnung || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        stundensatz: form.stundensatz === '' ? null : parseFloat(form.stundensatz),
        abrechnungsart: form.abrechnungsart || 'Zeitstunde',
        pauschale: form.pauschale === '' ? null : parseFloat(form.pauschale),
        notes: form.notes || null,
        document: form.document,
      };
      const res = editId
        ? await honorarkraefteAPI.updateContract(hk.id, editId, payload)
        : await honorarkraefteAPI.addContract(hk.id, payload);
      setHk(res.data);
      onChanged?.();
      setShowForm(false);
      toast.success('Vertrag gespeichert');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const del = async (cid) => {
    try {
      const res = await honorarkraefteAPI.deleteContract(hk.id, cid);
      setHk(res.data);
      onChanged?.();
      toast.success('Vertrag entfernt');
    } catch { toast.error('Fehler beim Entfernen'); }
  };

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="contracts-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Honorarverträge – {hk.first_name} {hk.last_name}</DialogTitle>
          <DialogDescription className="sr-only">Honorarverträge mit Laufzeit und Preis verwalten.</DialogDescription>
        </DialogHeader>

        {!showForm && (
          <div className="space-y-4">
            {contracts.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center" data-testid="contracts-empty">Noch keine Verträge hinterlegt.</p>
            ) : (
              ['rahmenvertrag', 'zusatz', 'einzelbeauftragung'].map((artKey) => {
                const list = contracts.filter((c) => (c.art || 'einzelbeauftragung') === artKey);
                if (list.length === 0) return null;
                return (
                  <div key={artKey} className="space-y-2" data-testid={`contract-group-${artKey}`}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{ART_LABEL[artKey]}</p>
                    {list.map((c) => (
                      <div key={c.id} className="flex items-start justify-between gap-3 rounded-md border p-3" data-testid={`contract-row-${c.id}`}>
                        <div className="space-y-1">
                          <div className="font-medium text-sm">{c.bezeichnung || ART_LABEL[c.art || 'einzelbeauftragung']}</div>
                          <div className="text-xs text-muted-foreground">
                            {c.start_date ? fmtDate(c.start_date) : '—'} – {c.end_date ? fmtDate(c.end_date) : 'unbefristet'}
                          </div>
                          <div className="flex flex-wrap gap-1 pt-1">
                            {c.stundensatz != null && <Badge variant="secondary" className="text-xs">{fmtEUR(c.stundensatz)}/{c.abrechnungsart === 'UE' ? 'UE' : 'Std.'}</Badge>}
                            {c.abrechnungsart === 'UE' && <Badge variant="outline" className="text-xs">UE = 45 Min.</Badge>}
                            {c.pauschale != null && <Badge variant="secondary" className="text-xs">Pauschale {fmtEUR(c.pauschale)}</Badge>}
                            {c.document?.url && (
                              <a href={`${BACKEND_URL}${c.document.url}?token=${token}`} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`contract-doc-${c.id}`}>
                                <FileText className="h-3 w-3" /> Vertrag
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-contract-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(c.id)} data-testid={`delete-contract-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            )}

            {contracts.some((c) => c.art === 'rahmenvertrag') && (() => {
              const year = new Date().getFullYear();
              const covered = new Set(
                contracts
                  .filter((c) => c.art === 'einzelbeauftragung' && c.start_date && new Date(c.start_date).getFullYear() === year)
                  .map((c) => new Date(c.start_date).getMonth()),
              );
              return (
                <div className="rounded-lg border p-3 space-y-2 bg-secondary/20" data-testid="monthly-overview">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Einzelbeauftragungen {year}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {MONTHS.map((m, idx) => {
                      const ok = covered.has(idx);
                      return (
                        <div key={idx} className={`flex items-center justify-between rounded-md border px-2 py-1.5 text-sm ${ok ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-900' : ''}`} data-testid={`month-${idx}`}>
                          <span className="truncate">{m.slice(0, 3)}</span>
                          {ok
                            ? <Badge variant="outline" className="text-[10px] border-emerald-400 text-emerald-700 dark:text-emerald-300">erledigt</Badge>
                            : <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => createMonthly(year, idx)} data-testid={`create-month-${idx}`}>+ anlegen</Button>}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-muted-foreground">Satz &amp; Abrechnungsart werden vom Rahmenvertrag übernommen. Dokumente über „Dokumente erzeugen".</p>
                </div>
              );
            })()}

            <Button className="rounded-full w-full" variant="outline" onClick={openNew} data-testid="add-contract-btn">
              <Plus className="h-4 w-4 mr-2" /> Vertrag hinzufügen
            </Button>
          </div>
        )}

        {showForm && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Art des Vertrags</Label>
              <Select value={form.art} onValueChange={(v) => set('art', v)}>
                <SelectTrigger data-testid="contract-art"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rahmenvertrag" data-testid="art-rahmenvertrag">Rahmenvertrag (längere Laufzeit)</SelectItem>
                  <SelectItem value="einzelbeauftragung" data-testid="art-einzelbeauftragung">Einzelbeauftragung (monatlich)</SelectItem>
                  <SelectItem value="zusatz" data-testid="art-zusatz">Zusatz / Startdokument</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Bezeichnung (optional)</Label>
              <Input value={form.bezeichnung} onChange={(e) => set('bezeichnung', e.target.value)} placeholder="z. B. Rahmenvertrag 2026" data-testid="contract-bezeichnung" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Laufzeit von</Label>
                <Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="contract-start" />
              </div>
              <div className="space-y-2">
                <Label>Laufzeit bis (leer = unbefristet)</Label>
                <Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} data-testid="contract-end" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Abrechnungsart</Label>
                <Select value={form.abrechnungsart} onValueChange={(v) => set('abrechnungsart', v)}>
                  <SelectTrigger data-testid="contract-abrechnungsart"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Zeitstunde" data-testid="abrechnungsart-zeitstunde">Zeitstunde (60 Min.)</SelectItem>
                    <SelectItem value="UE" data-testid="abrechnungsart-ue">Unterrichtseinheit UE (45 Min.)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{form.abrechnungsart === 'UE' ? 'Satz (€/UE)' : 'Satz (€/Std.)'}</Label>
                <Input type="number" min="0" step="0.01" value={form.stundensatz} onChange={(e) => set('stundensatz', e.target.value)} placeholder="z. B. 45" data-testid="contract-stundensatz" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Pauschale (€)</Label>
                <Input type="number" min="0" step="0.01" value={form.pauschale} onChange={(e) => set('pauschale', e.target.value)} placeholder="z. B. 1500" data-testid="contract-pauschale" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Der Satz gilt je nach Abrechnungsart pro UE (45 Min.) oder pro Zeitstunde (60 Min.). Stundensatz und Pauschale können einzeln oder kombiniert angegeben werden.</p>
            <div className="space-y-2">
              <Label>Vertrag (PDF)</Label>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" asChild>
                  <label className="cursor-pointer">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Datei wählen
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={onFile} data-testid="contract-file-input" />
                  </label>
                </Button>
                {form.document?.original_filename && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><FileText className="h-3 w-3" /> {form.document.original_filename}</span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notizen</Label>
              <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} data-testid="contract-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" className="rounded-full" onClick={() => setShowForm(false)} disabled={saving}>Zurück</Button>
              <Button className="rounded-full" onClick={save} disabled={saving || uploading} data-testid="save-contract-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Vertrag speichern'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
